// Per-box regroup editor (#1178 §7 step 6). Stage-and-save UX: the
// logist picks new group ordinals from the BoxGroupAssignDropdown for
// each visible item, then commits the whole batch via one PATCH. Dirty
// state participates in the shared `useUnsavedChangesGuard` so closing
// the drawer or navigating away prompts to discard.
//
// The editor owns its `useBoxRegroupDraft` and `useRegroupBoxItems`
// instances. Parent (`RequestBoxesView`) renders this side-by-side with
// the read table; the read table is fed the staged-effective ordinals
// so the manager-style grouping/dividers reflect pending changes in
// real time.

import { useMemo, useState, type CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { BoxGroupOverrideConflict } from '@magimp/shared';
import { BoxGroupOverrideConflictSchema, ErrorCode } from '@magimp/shared';
import { ApiClientError } from '../../../../api/errors.js';
import { Button, useNarrow } from '../../../../ui/index.js';
import { useUnsavedChangesGuard } from '../../../../ui/index.js';
import { useToastStore } from '../../../../stores/toast.js';
import { requestsKeys, useRegroupBoxItems } from '../../queries.js';
import { useBoxRegroupDraft, type RegroupBaselineItem } from '../../useBoxRegroupDraft.js';
import type { RequestBoxItemView } from '../../types.js';
import { BoxGroupAssignDropdown } from '../BoxGroupAssignDropdown.js';

export interface BoxRegroupEditorProps {
  requestId: string;
  boxId: string;
  /** Items already filtered by `isVisibleBoxItem` (parent-side). */
  items: ReadonlyArray<RequestBoxItemView>;
  /**
   * Raw ISO from `RequestBox.updatedAt` — required precondition for
   * the PATCH (#1178 D7). Parent gates rendering on `updatedAt != null`
   * via `canRegroupBoxItems`, so this is always a defined string here.
   */
  boxUpdatedAt: string;
  /** Called after a successful save or explicit Cancel. */
  onClose: () => void;
}

export function BoxRegroupEditor({
  requestId,
  boxId,
  items,
  boxUpdatedAt,
  onClose,
}: BoxRegroupEditorProps) {
  const { t } = useTranslation(['requests', 'common']);
  const narrow = useNarrow();
  const showToast = useToastStore((s) => s.show);
  const qc = useQueryClient();

  const baseline = useMemo<RegroupBaselineItem[]>(
    () => items.map((it) => ({ itemId: it.id, groupOrdinal: it.groupOrdinal })),
    [items],
  );
  const draft = useBoxRegroupDraft({ baseline });
  const mutation = useRegroupBoxItems(requestId);
  const [conflicts, setConflicts] = useState<ReadonlyArray<BoxGroupOverrideConflict>>([]);

  // INVARIANT (#1178): the unsaved-changes guard must reflect both the
  //   stage `dirty` and the in-flight `busy` so a parallel close / route
  //   change can decide whether to prompt or block.
  useUnsavedChangesGuard({
    dirty: draft.dirty,
    busy: mutation.isPending,
    onConfirmClose: onClose,
  });

  const onSave = () => {
    setConflicts([]);
    if (draft.batch.length === 0) {
      onClose();
      return;
    }
    mutation.mutate(
      { boxId, ifMatch: boxUpdatedAt, items: draft.batch },
      {
        onSuccess: () => {
          draft.reset();
          showToast({ tone: 'success', text: t('requests:boxRegroup.toast.saved') });
          onClose();
        },
        onError: (err) => {
          if (err instanceof ApiClientError) {
            // 412 / box.version.stale — caller's `If-Match` predated a
            //   concurrent write (D7/AC5). Per issue §8 case 27 we
            //   invalidate the detail (forcing a refetch with the latest
            //   `box.updatedAt`) AND discard the local stage so the
            //   logist sees authoritative server state before retrying.
            //   Without the invalidate the user would be stuck in a
            //   stale-stale loop; without the reset they would re-apply
            //   moves against an already-resolved conflict.
            if (
              err.httpStatus === 412 &&
              err.code === ErrorCode.DOMAIN_CONFLICT &&
              err.meta?.['kind'] === 'box.version.stale'
            ) {
              void qc.invalidateQueries({ queryKey: requestsKeys.detail(requestId) });
              draft.reset();
              showToast({ tone: 'error', text: t('requests:boxRegroup.toast.stale') });
              return;
            }
            // 409 / box.regroup.override_conflict — surface the conflict
            //   list inline so the logist can see exactly which carriers
            //   pin which groups (D13). Stage stays intact.
            if (
              err.httpStatus === 409 &&
              err.code === ErrorCode.DOMAIN_CONFLICT &&
              err.meta?.['kind'] === 'box.regroup.override_conflict'
            ) {
              const parsed = parseConflicts(err.meta?.['conflicts']);
              setConflicts(parsed);
              showToast({
                tone: 'error',
                text: t('requests:boxRegroup.toast.overrideConflict'),
              });
              return;
            }
          }
          showToast({ tone: 'error', text: t('requests:boxRegroup.toast.failed') });
        },
      },
    );
  };

  const onCancel = () => {
    if (mutation.isPending) return;
    draft.reset();
    setConflicts([]);
    onClose();
  };

  return (
    <div data-testid={`box-regroup-editor-${boxId}`} style={editorWrapStyle}>
      <header style={editorHeaderStyle}>
        <span style={editorTitleStyle}>{t('requests:boxRegroup.editor.title')}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            kind="ghost"
            size="sm"
            onClick={onCancel}
            disabled={mutation.isPending}
            data-testid={`box-regroup-cancel-${boxId}`}
          >
            {t('common:actions.cancel')}
          </Button>
          <Button
            kind="primary"
            size="sm"
            onClick={onSave}
            disabled={mutation.isPending || !draft.dirty}
            data-testid={`box-regroup-save-${boxId}`}
          >
            {t('common:actions.save')}
          </Button>
        </div>
      </header>
      <div
        style={{
          ...editorListStyle,
          gridTemplateColumns: narrow ? '1fr' : 'minmax(180px, 2fr) minmax(180px, 1fr)',
        }}
      >
        {items.map((it) => (
          <div key={it.id} style={editorRowStyle}>
            <div style={editorRowLabelStyle}>
              <span style={editorRowNameStyle}>{it.name || '—'}</span>
              {it.brand.length > 0 && <span style={editorRowMetaStyle}>{it.brand}</span>}
            </div>
            <BoxGroupAssignDropdown
              value={draft.effective(it.id)}
              existingGroupOrdinals={draft.existingGroupOrdinals}
              nextNewGroupOrdinal={draft.nextNewGroupOrdinal}
              disabled={mutation.isPending}
              testId={`box-regroup-select-${boxId}-${it.id}`}
              selectStyle={{ width: '100%' }}
              onChange={(next) => draft.stage(it.id, next)}
            />
          </div>
        ))}
      </div>
      {conflicts.length > 0 && (
        <div data-testid={`box-regroup-conflicts-${boxId}`} style={conflictBoxStyle}>
          <div style={conflictTitleStyle}>{t('requests:boxRegroup.conflicts.title')}</div>
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
            {conflicts.map((c) => (
              <li key={`${c.boxId}-${c.groupOrdinal}-${c.domainKind}`}>
                {t('requests:boxRegroup.conflicts.line', {
                  ordinal: c.groupOrdinal,
                  domain: t(
                    c.domainKind === 'bid'
                      ? 'requests:boxRegroup.conflicts.domain.bid'
                      : 'requests:boxRegroup.conflicts.domain.insurance',
                  ),
                  carriers: c.conflictingCarriers.map((cc) => cc.carrierName).join(', '),
                })}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function parseConflicts(raw: unknown): BoxGroupOverrideConflict[] {
  if (!Array.isArray(raw)) return [];
  const out: BoxGroupOverrideConflict[] = [];
  for (const entry of raw) {
    const parsed = BoxGroupOverrideConflictSchema.safeParse(entry);
    if (parsed.success) out.push(parsed.data);
  }
  return out;
}

const editorWrapStyle: CSSProperties = {
  marginTop: 10,
  border: '1px solid var(--border-1)',
  borderRadius: 6,
  background: 'var(--bg-1)',
  padding: 10,
  display: 'grid',
  gap: 8,
};

const editorHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
};

const editorTitleStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '.04em',
  color: 'var(--fg-2)',
};

const editorListStyle: CSSProperties = {
  display: 'grid',
  gap: 6,
};

const editorRowStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(180px, 2fr) minmax(180px, 1fr)',
  alignItems: 'center',
  gap: 12,
  padding: '6px 8px',
  borderTop: '1px solid var(--border-2)',
};

const editorRowLabelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
};

const editorRowNameStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--fg-1)',
  wordBreak: 'break-word',
};

const editorRowMetaStyle: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-3)',
};

const conflictBoxStyle: CSSProperties = {
  border: '1px solid var(--border-1)',
  borderRadius: 4,
  background: 'var(--bg-2)',
  padding: 8,
  fontSize: 12,
  color: 'var(--fg-1)',
};

const conflictTitleStyle: CSSProperties = {
  fontWeight: 600,
  fontSize: 12,
};
